import { TOOLTIP_ERROR_LIST_CLASS } from '@components/Button/SaveButton/util';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports
import { EL_DIALOG_INNER_CONTENT } from '@components/Dialog/constants';
import {
  FULL_SCREEN_DIALOG_ID,
  MASTERFIND_DIALOG_ID,
} from '@components/Dialog/util';
import { BODY_FONT_SIZE } from '@components/Theme/constants';
import { visuallyHiddenStyle } from '@components/VisuallyHidden';
import { Global } from '@emotion/react';
import { ENV_VARS } from '@env';

import { FULLSTORY_DEBUG_ENABLED } from '@hooks/useFullstoryDebug';
import { useInputStyle } from '@hooks/useInputStyle';
import { useOutlineRing } from '@hooks/useOutlineRing';
import { useTheme } from '@hooks/useTheme';
// @reach/menu-button/styles.css removed — react-aria-components replaces @reach
import { MODAL_BACKGROUND, REACH_MENU } from '@utils/zIndex';
import { round } from 'lodash-es';
import { FC } from 'react';
import { clock5BgImg, clock5BgImgDark } from './iconBgImg';

const DEFAULT_LINE_HEIGHT = `${round(BODY_FONT_SIZE * 1.3)}px`;

export const AppStyles: FC = () => {
  const {
    colors,
    gray,
    fonts,
    tooltip,
    contextMenu,
    formElement,
    system,
    hover,
    shell,
  } = useTheme();
  const inputStyle = useInputStyle();
  const getOutLineRing = useOutlineRing();

  return (
    <Global
      styles={{
        '*': {
          boxSizing: 'border-box',
          '&:focus:not(:focus-visible)': {
            outline: 'none',
          },
          '&:focus-visible': {
            outline: getOutLineRing(),
          },
          '.focusable:focus-visible': {
            outline: getOutLineRing(),
          },
          '.validation-error:focus-visible': {
            outline: getOutLineRing(colors.error),
          },
          '.validation-warningDark:focus-visible': {
            outline: getOutLineRing(colors.warningDark),
          },
          '.validation-success:focus-visible': {
            outline: getOutLineRing(colors.accent),
          },
          fontFamily: FULLSTORY_DEBUG_ENABLED
            ? 'Redacted !important'
            : undefined,
        },
        // `html > body` outranks third-party `body` rules (e.g. the embedded Dash app's) regardless of load order.
        'html > body': {
          padding: 0,
          fontFamily: fonts.main,
          webkitFontSmoothing: 'antialiased',
          mozOsxFontSmoothing: 'grayscale',
          fontSize: BODY_FONT_SIZE,
          lineHeight: DEFAULT_LINE_HEIGHT,
          color: colors.text,
        },
        '#overlay': {
          display: 'var(--overlay-display)',
          background: 'var(--overlay-bg)',
          position: 'fixed',
          width: '100%',
          height: '100vh',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 5,
          textAlign: 'center',
          fontWeight: 'bold',
        },
        '#connected-tabs': {
          display: 'var(--connected-tabs-display)',
          background: 'var(--connected-tabs-bg)',
          fontWeight: 'bold',
          textAlign: 'center',
          width: '100%',
          height: 20,
        },
        a: {
          color: colors.text,
        },
        '[class*="spectrum-Dialog-content"]': {
          overflowY: 'unset !important' as anyOk,
          lineHeight: `${DEFAULT_LINE_HEIGHT} !important` as anyOk,
          fontSize: `${BODY_FONT_SIZE.toString()}px !important` as anyOk,
          fontWeight: 'normal !important' as anyOk,
          padding: '0px !important',
          maxWidth: '100%',
        },
        // Yeah this is pretty brittle, but it's the best css only way to target this for table expand mode as of writing.
        [`[class*=spectrum][lang]:has([data-dialog-id="${FULL_SCREEN_DIALOG_ID}"]) [class*=spectrum-Underlay]`]:
          {
            background: `none !important`,
          },
        [`[class*=spectrum][lang]:has([data-dialog-id="${MASTERFIND_DIALOG_ID}"]) [class*=spectrum-Underlay]`]:
          {
            background: `none !important`,
            pointerEvents: 'none !important' as anyOk,
          },
        [`[class*=spectrum][lang]:has([data-blur="true"][data-dialog-show]) [class*=spectrum-Underlay]`]:
          {
            backdropFilter: 'blur(10px)',
          },
        [`[data-testid="modal"]:has(#${MASTERFIND_DIALOG_ID}-dialog)`]: {
          transition: 'none !important',
          border: 0,
        },
        // this only targets dialog v2
        '[data-testid=dialog][role=dialog]': {
          height: '100%',
          /** Override default spectrum overflow. Allows things like dropdowns to be scrolled to https://masterysys.atlassian.net/browse/ME-264672 */
          overflow: 'visible',
          // setting padding will have no effect from consumers
          padding: '0px !important',
          // setting margin will have no effect from consumers
          margin: '0 !important',
        },
        '[class*=spectrum-Textfield]': {
          fontFamily: `${fonts.main} !important`,
          background: `#FFFFFF !important`,
          'html.theme-dark &': {
            background: `#46464A !important`,
          },
        },
        '[class*=spectrum-Textfield] input, [class*=spectrum-Textfield] [role="group"], [class*=spectrum-Textfield] [role="group"] *':
          {
            color: `${gray[30]} !important`,
            'html.theme-dark &': {
              color: `#F5F5F5 !important`,
            },
          },
        '[class*=spectrum-Menu-item]': {
          cursor: 'pointer',
        },
        '[class*="spectrum-Dialog--small"][data-testid="dialog"]': {
          minWidth: '360px',
          width: 'unset',
        },
        '[class*="spectrum-Dialog-grid"]': {
          gridTemplateRows: '1fr !important',
          gridTemplateColumns: '1fr !important',
          gridTemplateAreas: 'none !important',
          placeContent: 'center !important',
          display: 'flex !important',
        },
        '[data-testid="modal"]': {
          border: `2px solid ${gray[99]}`,
          borderRadius: '4px',
          display: 'flex',
        },
        [`[data-testid="modal"]:has(#${FULL_SCREEN_DIALOG_ID}-dialog)`]: {
          maxWidth: '100vw !important',
          maxHeight: '100vh !important',
          transition: 'none !important',
          border: '0px !important',
          '[data-testid="dialog-content"]': {
            padding: '0px !important',
          },
          [`#${EL_DIALOG_INNER_CONTENT}`]: {
            padding: '46px 46px 20px !important',
            background: gray[95],
          },
          '[class*="spectrum-Dialog-content"]': {
            maxWidth: '100vw !important',
            maxHeight: '100vh !important',
            width: '100vw !important',
            height: '100vh  !important',
            display: 'grid',
            borderRadius: 0,
          },
          '[data-dialog-close]': {
            top: 0,
            right: 0,
            width: 48,
            height: 46,
          },
        },
        address: {
          display: 'inline-block',
          fontStyle: 'inherit',
        },
        code: { fontFamily: fonts.code },
        pre: {
          fontFamily: 'inherit',
          whiteSpace: 'pre',
          display: 'inline',
          margin: 0,
        },
        ul: {
          margin: 0,
          padding: 0,
        },
        button: {
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          font: 'unset',
          fontFamily: fonts.main,
          color: 'inherit',
          padding: 0,
          fontWeight: 'normal',
          margin: 0,
          lineHeight: DEFAULT_LINE_HEIGHT,
        },
        p: { margin: 0 },
        h1: {
          margin: 0,
          paddingRight: '4px',
          fontSize: '24px',
        },
        input: inputStyle,
        '.input': inputStyle,
        'input[type="checkbox"]': {
          width: 'auto',
        },
        'input.timeInput': {
          background: `${clock5BgImg} !important`,

          // Dark mode override when <html class="theme-dark">
          'html.theme-dark &': {
            background: `#46464A ${clock5BgImgDark} !important`,
          },
        },
        textarea: {
          ...inputStyle,
          display: 'block',
          minHeight: '80px',
        },
        select: {
          ...inputStyle,
          appearance: 'none',
          cursor: 'pointer',
          padding: '8px 28px 8px 8px',
        },
        '[data-readonly="true"]': {
          ...inputStyle,
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          height: 'calc(1em + 20px)',
          borderColor: gray[90],
          backgroundColor: formElement.readOnly.background,
        },
        '[data-readonly="true"][data-input="TextArea"]': {
          ...inputStyle,
          display: 'block',
          minHeight: '80px',
          height: 'auto',
          overflow: 'auto',
          borderColor: gray[90],
          backgroundColor: formElement.readOnly.background,
        },
        label: {
          fontWeight: 500,
          whiteSpace: 'nowrap',
          display: 'inline-block',
          padding: '0 0 6px 0',
          cursor: 'pointer',
        },
        fieldset: {
          border: 0,
          padding: 0,
          margin: 0,
          minWidth: 0,
        },
        legend: {
          border: 0,
          padding: 0,
          margin: 0,
          minWidth: 0,
        },
        '.table-striped': {
          width: '100%',
          borderCollapse: 'collapse',
          borderColor: gray[90],

          th: {
            padding: '8px 16px',
            textAlign: 'left',
          },

          tbody: {
            '& tr:nth-of-type(odd) td': {
              background: gray[95],
              borderBottom: `1px solid ${gray[80]}`,
            },
          },
          td: {
            padding: '8px 16px',
            wordWrap: 'break-word',

            '&:last-of-type': { textAlign: 'right' },
          },
        },
        '#spectrum-provider': {
          width: '100%',
          display: 'table',
        },
        '[class*="_spectrum"][lang]': {
          background: 'none !important',
          isolation: 'auto !important' as anyOk,
        },
        '[class*="spectrum_"]': {
          isolation: 'auto !important' as anyOk,
        },
        '[class*="spectrum-Underlay--transparent"]': {
          pointerEvents: 'none !important' as anyOk,
        },
        '[class*=spectrum-FocusRing-ring]:after': {
          display: 'none !important',
        },
        '[class*=_focus-ring]:before': {
          display: 'none !important',
        },
        '#walkme-player': visuallyHiddenStyle,
        '#walkme-player-panel': { display: 'none !important' as anyOk },
        // Dash's own body rule sets a light background; keep it consistent with the app shell in dark mode.
        'html.theme-dark body[data-dash-body]': {
          background: `${shell.content.background} !important`,
        },
        // WalkMe survey/feedback popups are designed for light mode only.
        // Our dark mode global styles apply a dark background to all inputs/textareas
        // which makes text invisible in WalkMe's dark-text survey fields.
        // Hardcoded hex values are intentional: theme CSS variables remap in dark mode
        // (e.g. gray.white → #1B1B1F), so we must use static light-mode colors here.
        '[class*="walkme-"] input, [class*="walkme-"] textarea, [class*="wm-"] input, [class*="wm-"] textarea':
          {
            'html.theme-dark &': {
              background: `#FFFFFF !important`,
              color: `#333333 !important`,
            },
          },
        '.Toastify__toast-body': {
          whiteSpace: 'pre-wrap',
        },
        '.Toastify__toast-container': {
          zIndex: '200000 !important',
        },
        '.Toastify__toast--warning': {
          color: system.toast.warning.text,
          background: system.toast.warning.background,
          border: `solid ${system.toast.warning.accent}`,
        },
        '.Toastify__toast--info': {
          background: system.toast.info.background,
          border: `solid ${system.toast.info.accent}`,
          color: system.toast.info.text,
        },
        '.Toastify__toast--error': {
          background: system.toast.error.background,
          border: `solid ${system.toast.error.accent}`,
          color: system.toast.error.text,
        },
        '.Toastify__toast--success': {
          background: system.toast.success.background,
          border: `solid ${system.toast.success.accent}`,
          color: system.toast.success.text,
        },
        '.Toastify__toast': {
          borderWidth: '1px 1px 1px 8px',
          borderRadius: '4px',
        },
        '[data-reach-dialog-content]': {
          overflow: 'auto',
        },
        '[data-reach-tab-list]': {
          background: 'none',
          fontWeight: '400 !important',
        },
        'div[data-reach-tooltip]': {
          ...tooltip,
        },
        [`.${TOOLTIP_ERROR_LIST_CLASS}`]: {
          padding: '0 0 0 10px',
        },
        ['.fs-unmask, .fs-unmask *']: {
          fontFamily: `${fonts.main} !important`,
        },
        ['.fs-mask, .fs-mask *']: {
          fontFamily: FULLSTORY_DEBUG_ENABLED
            ? 'Redacted !important'
            : undefined,
        },
        ['.fs-mask *']: ENV_VARS.VITE_DEBUG_FS_MASK && {
          textShadow: 'none',
        },
        // ---------- BEGIN: replacement for removed @reach/menu-button/styles.css ----------
        // These rules replicate the base styles that @reach/menu-button/styles.css
        // provided. Our global overrides below (e.g. [data-reach-menu-list]) were
        // originally layered ON TOP of the @reach defaults, so without these the
        // menus lose positioning, link decoration reset, cursor, etc.
        //
        // NOTE: Do NOT set `position: absolute` on [data-reach-menu-popover] or
        // `position: relative` on [data-reach-menu]. React-aria's Popover uses
        // `position: fixed` with viewport-based top/left for portal positioning.
        // Overriding to `absolute` breaks placement (menu opens to the right
        // instead of aligning to the trigger).
        '[data-reach-menu]': {
          zIndex: REACH_MENU,
          fontFamily: fonts.main,
        },
        '[data-reach-menu-popover][hidden]': {
          display: 'none',
        },
        // Suppress the focus ring on menu trigger buttons after the menu
        // closes. React-aria returns focus to the trigger, which fires the
        // global *:focus-visible outline.
        '[data-reach-menu-button]': {
          '&:focus, &:focus-visible': {
            outline: 'none !important',
          },
        },
        // ---------- END: replacement for removed @reach/menu-button/styles.css ----------
        '[class*=spectrum-Underlay]': {
          zIndex: `${MODAL_BACKGROUND} !important`,
        },
        ['[class*=spectrum-Modal-wrapper]']: {
          zIndex: `${MODAL_BACKGROUND} !important`,
        },
        '[data-testid="modal"]:has([data-dialog-show="false"])': {
          // prevents a flash of primary modal with no content before a secondary is shown
          border: 0,
        },
        '[class*=spectrum-ActionButton] svg': {
          marginRight: '6px !important',
        },
        '[data-datepicker-variant="dateonly"]': {
          fontSize: '12px !important',
          width: '100% !important',
          fontFamily: `${fonts.main}  !important`,
        },
        '[data-datepicker-variant="years"]': {
          fontSize: '12px !important',
          width: '115px !important',
          minWidth: '110px !important',
          fontFamily: `${fonts.main}  !important`,
        },
        '[data-reach-menu-item][aria-disabled]': {
          opacity: '1 !important',
        },
        '[data-reach-tab]:disabled': {
          opacity: '1 !important',
        },
        '[data-reach-tab][data-selected]': {
          fontWeight: '500 !important',
        },
        '[data-reach-menu-list]': {
          // Base styles (originally from @reach/menu-button/styles.css)
          display: 'block',
          whiteSpace: 'nowrap',
          outline: 'none !important' as fixMe,
          '&:focus, &:focus-visible': {
            outline: 'none !important',
          },
          // App overrides
          border: 0,
          padding: 0,
          fontWeight: 500,
          overflow: 'hidden',
          borderRadius: 2,
          boxShadow: '0 0 7px 5px rgba(0, 0, 0, 0.03)',
          ...contextMenu,
          // <a>-based MenuItems (react-aria MenuItem with href) are inline by
          // default — force block so they stack vertically in header menus.
          // Also reset link-specific defaults (color, font, text-decoration)
          // so <a> items match the surrounding menu text style.
          // Context-menu items render as <div> and set their own inline
          // styles, so these rules intentionally target only <a> tags.
          'a[data-reach-menu-item]': {
            display: 'block',
            color: 'inherit',
            font: 'inherit',
            textDecoration: 'none',
          },
          '[data-reach-menu-item]': {
            // Base styles (originally from @reach/menu-button/styles.css)
            userSelect: 'none',
            cursor: 'pointer',
            // The global *:focus-visible rule uses !important, so we must
            // also use !important here to suppress the outline ring on menu
            // items. Visual feedback comes from [data-focused] styling.
            outline: 'none !important' as fixMe,
            border: 'none',
            '&:focus, &:focus-visible': {
              outline: 'none !important',
            },
            // react-aria sets data-disabled on disabled items
            '&[data-disabled]': {
              cursor: 'not-allowed',
            },
            // App overrides
            fontWeight: 400,
            whiteSpace: 'nowrap',
            padding: '10px 15px 10px 5px',
            // react-aria uses [data-focused] for keyboard-highlighted items,
            // but it's "sticky" — it stays on the last item after the mouse
            // leaves.  Use [data-hovered] (mouse, clears on exit) and
            // [data-focus-visible] (keyboard) so the highlight clears
            // immediately when the cursor moves away.
            // Header menu items (no data-contextmenuitem) get the primary
            // color highlight. Context menu items manage their own
            // focused/hover colors via inline Emotion styles, so we
            // exclude them here to avoid specificity conflicts.
            '&[data-selected]:not([data-contextmenuitem]), &[data-hovered]:not([data-contextmenuitem]), &[data-focus-visible]:not([data-contextmenuitem]), &:focus-visible:not([data-contextmenuitem])':
              {
                background: hover.background,
                color: colors.primary,
                fontWeight: 600,
                letterSpacing: '-0.09px !important',
              },
            '&[disabled]': {
              color: gray[90],
            },
          },
        },
        // '#content > *': {
        //   '-webkit-app-region': 'no-drag',
        // },
      }}
    />
  );
};
